import { useState, useCallback } from 'react';
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption } from '@headlessui/react';
import type { Country } from '@/types';
import { useCountrySearch } from '@/hooks';
import { cn } from '@/lib/utils';
import { Check, Search } from 'lucide-react';

interface TypeAnswerProps {
  onSubmit: (code: string) => void;
  disabled?: boolean;
  correctCountry?: Country;
  showResult?: boolean;
  isCorrect?: boolean;
}

export function TypeAnswer({
  onSubmit,
  disabled = false,
  correctCountry,
  showResult = false,
  isCorrect,
}: TypeAnswerProps) {
  const [query, setQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const { search } = useCountrySearch();

  const results = query.length > 0 ? search(query) : [];

  const handleSelect = useCallback(
    (country: Country | null) => {
      if (!country || disabled) return;
      setSelectedCountry(country);
      setQuery(country.name);
      onSubmit(country.code);
    },
    [disabled, onSubmit]
  );

  return (
    <div className="space-y-4">
      <Combobox value={selectedCountry} onChange={handleSelect} disabled={disabled}>
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <ComboboxInput
              className={cn(
                'w-full pl-12 pr-4 py-4 text-lg rounded-xl border-2 transition-colors',
                'bg-white dark:bg-gray-900',
                'focus:outline-none focus:border-primary-500',
                !showResult && 'border-gray-200 dark:border-gray-700',
                showResult && isCorrect && 'border-success-500 bg-success-50 dark:bg-success-950/30',
                showResult && !isCorrect && 'border-error-500 bg-error-50 dark:bg-error-950/30',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
              onChange={(e) => setQuery(e.target.value)}
              displayValue={(country: Country | null) => country?.name ?? ''}
              placeholder="Type country name..."
              autoComplete="off"
            />
          </div>

          {results.length > 0 && !disabled && !selectedCountry && (
            <ComboboxOptions className="absolute z-10 w-full mt-2 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-60 overflow-auto">
              {results.map((country) => (
                <ComboboxOption
                  key={country.code}
                  value={country}
                  className={({ focus }) =>
                    cn(
                      'px-4 py-3 cursor-pointer transition-colors',
                      focus && 'bg-primary-50 dark:bg-primary-950/30'
                    )
                  }
                >
                  {({ selected }) => (
                    <div className="flex items-center justify-between">
                      <span className={cn(selected && 'font-semibold')}>
                        {country.name}
                      </span>
                      {selected && <Check className="w-5 h-5 text-primary-500" />}
                    </div>
                  )}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          )}
        </div>
      </Combobox>

      {showResult && !isCorrect && correctCountry && (
        <div className="p-4 bg-success-50 dark:bg-success-950/30 border border-success-300 dark:border-success-700 rounded-xl">
          <span className="text-sm text-success-700 dark:text-success-300">
            Correct answer: <strong>{correctCountry.name}</strong>
          </span>
        </div>
      )}
    </div>
  );
}
